/**
 * Stands and gates.
 *
 * The board shows the plan as it stands, with everything wrong with it — not
 * only with a new assignment. An allocation screen that checks a change and
 * not the plan cannot tell you that two things approved last week now
 * overlap because one of them moved, which is how a stand comes to be
 * double-booked on the morning it matters.
 *
 * The stands that are closed are closed by the REST OF THE PLATFORM: open
 * work orders on the apron, closures of the surface. Nothing here is typed
 * twice, and each refusal carries the reference so somebody can go and read
 * the order that caused it.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useSession } from "../../app/SessionContext";
import { useWorkOrders } from "../../api/queries";
import { bestKnown, flightName } from "../../domain/flights";
import {
  candidatesFor,
  standBoard,
  standUse,
  unallocated,
  STAND_KIND_LABEL,
  type StandRegister,
} from "../../domain/stands";
import { blocksFromWorkOrders } from "./standBlocks";
import { ApronView } from "./ApronView";
import { useFlightWorld, type SaveOptions } from "./useFlightWorld";
import { delayImpacts } from "../../domain/flightDelays";
import { Button, Card, EmptyState, Table } from "../../design/components";
import { Page } from "../../layout/AppShell";
import { DayState, FlightTheme, LiveStrip, ImpactList } from "./FlightTheme";
import { useScreenContext } from "../../assistant/screen";
import { Tiles, x as ex } from "../executive/shared";
import { t, tn } from "../../i18n";
import s from "./Flights.module.css";
import { hhmm } from "../../lib/format";

/** The note the register used to be held under. Nothing reads or writes it now; kept for the imports that name it. */
export const STANDS_TOPIC = "ops.stands";

export function StandsPage() {
  const session = useSession();
  /*
   * The flight record and the allocation plan: the same stands and
   * placements the allocation screen works on. A change made here is sent
   * row by row — a placement, a release, a stand added — never the whole
   * register over somebody else's.
   */
  const world = useFlightWorld();
  const flights = world.flights;
  const register = world.register;
  const [asking, setAsking] = useState<string | null>(null);
  const [saved, setSaved] = useState<{ ok: boolean; text: string } | null>(null);
  /* the map shows the outcome where the person acted; this line keeps the last one */
  const save = (next: StandRegister, opts?: SaveOptions) =>
    world.saveRegister(next, opts).then(
      () => setSaved({ ok: true, text: t("saved on the platform under {who}", { who: session.principal.name }) }),
      (e: unknown) => {
        setSaved({ ok: false, text: e instanceof Error ? e.message : String(e) });
        throw e;
      },
    );

  /*
   * The join. Open work orders close stands, and the flight layer is never
   * told separately — it reads the same register the work-orders screen does.
   */
  const orders = useWorkOrders();
  const blocks = useMemo(
    () => blocksFromWorkOrders(register.stands, orders.data?.items ?? []),
    [register.stands, orders.data],
  );

  const window = useMemo(() => {
    const times = flights
      .map((f) => bestKnown(f).at)
      .filter((x): x is string => Boolean(x))
      .sort();
    return { from: times[0] ?? new Date().toISOString(), to: times.at(-1) ?? new Date().toISOString() };
  }, [flights]);

  const rows = useMemo(() => standBoard(register, flights, blocks, window), [register, flights, blocks, window]);
  const impacts = useMemo(() => delayImpacts(flights, register, blocks, new Date()), [flights, register, blocks]);
  const unplaced = useMemo(() => unallocated(register, flights, window), [register, flights, window]);
  const use = useMemo(() => standUse(rows, window, unplaced.length), [rows, window, unplaced]);
  const conflicting = rows.filter((r) => r.conflicts.length > 0);

  const source = [{ kind: "entered" as const, ref: "stands", label: t("the stand plan") }];
  const tiles = [
    {
      key: "stands",
      label: t("stands"),
      value: use.stands,
      unit: "",
      text: t("{n} usable today", { n: use.usable }),
      tone: "good" as const,
      assumed: true,
      sources: source,
    },
    {
      key: "blocked",
      label: t("closed by other work"),
      value: use.blocked,
      unit: "",
      text: use.blocked
        ? t("Closed by a work order")
        : t("nothing is closed"),
      tone: use.blocked > 0 ? ("warn" as const) : ("good" as const),
      assumed: true,
      sources: source,
    },
    {
      key: "caution",
      label: t("stands with work on their apron"),
      value: rows.filter((r) => r.cautions.length > 0).length,
      unit: "",
      text: t("open work nearby; the stand is still usable"),
      assumed: true,
      sources: source,
    },
    {
      key: "conflicts",
      label: t("conflicts in the plan"),
      value: use.conflicts,
      unit: "",
      text: use.conflicts ? t("In the current plan") : t("the plan is clean"),
      tone: use.conflicts > 0 ? ("bad" as const) : ("good" as const),
      assumed: true,
      sources: source,
    },
    {
      key: "unallocated",
      label: t("movements with no stand"),
      value: use.unallocated,
      unit: "",
      text: use.unallocated ? t("each one needs a place to park") : t("every movement has a stand"),
      tone: use.unallocated > 0 ? ("warn" as const) : ("good" as const),
      assumed: true,
      sources: source,
    },
    {
      key: "use",
      label: t("stand use"),
      value: use.utilisationPct,
      unit: "%",
      text: t("of the usable stand-hours in the day"),
      assumed: true,
      sources: source,
    },
  ];

  const board = [
    t("{site} has {n}, {usable} of them usable today.", {
      site: session.site.code,
      n: tn(use.stands, "{n} stand", "{n} stands"),
      usable: use.usable,
    }),
  ];
  if (use.conflicts > 0) {
    board.push(tn(use.conflicts, "{n} conflict stands in the plan.", "{n} conflicts stand in the plan."));
  }
  if (use.blocked > 0) {
    board.push(
      tn(
        use.blocked,
        "{n} stand is closed by work elsewhere on the platform.",
        "{n} stands are closed by work elsewhere on the platform.",
      ),
    );
  }
  if (use.unallocated > 0) {
    board.push(tn(use.unallocated, "{n} movement has nowhere to park.", "{n} movements have nowhere to park."));
  }

  useScreenContext({
    route: "/stands",
    title: "Stands and gates",
    worksInFull: true,
    summary: board,
    figures: new Set(
      [use.stands, use.usable, use.blocked, use.conflicts, use.unallocated, use.utilisationPct ?? 0]
        .map(String)
        .concat(Array.from({ length: 13 }, (_, i) => String(i))),
    ),
    own: (q) => {
      const text = q.toLowerCase();
      if (/conflict|clash|double/.test(text)) {
        return conflicting.length
          ? conflicting.map((r) => `${r.stand.code}: ${r.conflicts.map((c) => c.why).join(" ")}`).join("\n")
          : t("The plan has no conflicts.");
      }
      if (/closed|blocked|out of service/.test(text)) {
        const shut = rows.filter((r) => r.blocked.length > 0 || r.stand.outOfService);
        return shut.length
          ? shut
              .map(
                (r) =>
                  `${r.stand.code}: ${r.blocked.map((b) => `${b.ref ?? ""} ${b.why}`).join("; ") || r.stand.outOfService?.why}`,
              )
              .join("\n")
          : t("Every stand is open.");
      }
      if (/park|unallocated|no stand/.test(text)) {
        return unplaced.length
          ? unplaced.map((f) => `${flightName(f)} — ${hhmm(bestKnown(f).at)}`).join("\n")
          : t("Every movement has a stand.");
      }
      return null;
    },
    offers: [
      { label: "What conflicts?", ask: "what conflicts in the plan?" },
      { label: "What is closed?", ask: "which stands are closed?" },
      { label: "What has nowhere to park?", ask: "which movements have no stand?" },
    ],
  });

  const asked = asking ? flights.find((f) => f.id === asking) : null;
  const options = asked
    ? candidatesFor({
        register,
        flights,
        blocks,
        flight: asked,
        window: {
          from: bestKnown(asked).at ?? window.from,
          to: new Date(Date.parse(bestKnown(asked).at ?? window.from) + 80 * 60_000).toISOString(),
        },
      })
    : [];

  const lede = (
    <p className={s.lede}>
      {t(
        "Where each flight parks, and any problems with the plan.",
      )}
    </p>
  );
  /* the stands come with the plan: without it there is no register to draw */
  if (world.loading || world.error || (!world.day && world.planError))
    return (
      <FlightTheme>
        <Page eyebrow={t("{site} · Flight operations", { site: session.site.code })} title="Stands and gates">
          {lede}
          <DayState world={world} needsPlan />
        </Page>
      </FlightTheme>
    );

  return (
    <FlightTheme>
      <Page eyebrow={t("{site} · Flight operations", { site: session.site.code })} title="Stands and gates">
        <LiveStrip flights={flights} site={session.site.code} />
        {lede}
        {/* no flights on the record: the stands are still there to see and to add to */}
        <DayState world={world} />
        <p className={s.note}>
          {t(
            "Stands closed by a work order are removed automatically. Work nearby shows as a caution.",
          )}
        </p>

        <Tiles items={tiles} currency="SAR" />

        <ApronView
          register={register}
          flights={flights}
          blocks={blocks}
          rows={rows}
          waiting={unplaced}
          who={session.principal.name}
          onSave={save}
        />

        <Card
          collapsible
          title="Knock-on from today's delays"
          sub="Delays that push into other flights' stands or gates"
          testId="stand-knockon"
        >
          <ImpactList impacts={impacts} empty="No delay is running into another movement's stand or gate." />
        </Card>

        {conflicting.length > 0 && (
          <Card collapsible title="What is wrong with the plan" sub="found in what is already assigned">
            <ul className={s.conflicts} data-testid="stand-conflicts">
              {conflicting.map((r) =>
                r.conflicts.map((c) => (
                  <li key={`${r.stand.code}-${c.kind}-${c.why}`} className={s.conflict} data-kind={c.kind}>
                    <span className={s.standCode}>{r.stand.code}</span>
                    <span>
                      {c.why}
                      {c.ref && (
                        <span className={s.why}>
                          {" "}
                          · <Link to="/work-orders">{c.ref}</Link>
                        </span>
                      )}
                    </span>
                  </li>
                )),
              )}
            </ul>
          </Card>
        )}

        {unplaced.length > 0 && (
          <Card collapsible title="Movements with nowhere to park" sub="ask the board where each one could go">
            <Table>
              <thead>
                <tr>
                  <th>{t("Flight")}</th>
                  <th className={s.num}>{t("Due")}</th>
                  <th>{t("Aircraft")}</th>
                  <th />
                </tr>
              </thead>
              <tbody data-testid="stands-unallocated">
                {unplaced.map((f) => (
                  <tr key={f.id}>
                    <td>
                      <b>{flightName(f)}</b>
                      <div className={s.why}>{f.otherAirport}</div>
                    </td>
                    <td className={s.num}>{hhmm(bestKnown(f).at)}</td>
                    <td className={s.mono}>
                      {f.aircraftIcao}
                      {f.size && <span className={s.size}>{f.size}</span>}
                    </td>
                    <td>
                      <Button
                        size="sm"
                        data-testid={`ask-${flightName(f)}`}
                        onClick={() => setAsking(f.id === asking ? null : f.id)}
                      >
                        {f.id === asking ? t("Close") : t("Where can it go?")}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>

            {asked && (
              <div className={s.candidates} data-testid="stand-candidates">
                <h3 className={s.groupName}>{t("Where {flight} could go", { flight: flightName(asked) })}</h3>
                <Table>
                  <thead>
                    <tr>
                      <th>{t("Stand")}</th>
                      <th>{t("Kind")}</th>
                      <th className={s.num}>{t("Takes")}</th>
                      <th>{t("Verdict")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {options.map((c) => (
                      <tr key={c.stand.code}>
                        <td className={s.standCode}>{c.stand.code}</td>
                        <td className={s.why}>{t(STAND_KIND_LABEL[c.stand.kind])}</td>
                        <td className={s.num}>{t("code {c}", { c: c.stand.maxSize })}</td>
                        <td>
                          {c.clear ? (
                            <span className={s.good}>{c.because}</span>
                          ) : (
                            <span className={s.bad}>{c.conflicts.map((x) => x.why).join(" ")}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                <p className={s.meta}>
                  {t(
                    "Contact stands come first, then the smallest stand that fits.",
                  )}
                </p>
              </div>
            )}
          </Card>
        )}

        <Card collapsible title="The board" sub="Every stand and what's on it">
          <Table>
            <thead>
              <tr>
                <th>{t("Stand")}</th>
                <th>{t("Kind")}</th>
                <th className={s.num}>{t("Takes")}</th>
                <th>{t("On it today")}</th>
                <th>{t("State")}</th>
              </tr>
            </thead>
            <tbody data-testid="stands-board">
              {rows.map((r) => (
                <tr key={r.stand.code} data-testid={`stand-${r.stand.code}`}>
                  <td className={s.standCode}>
                    {r.stand.code}
                    {r.stand.bridge && <span className={s.why}>{t("air bridge")}</span>}
                  </td>
                  <td className={s.why}>{t(STAND_KIND_LABEL[r.stand.kind])}</td>
                  <td className={s.num}>{t("code {c}", { c: r.stand.maxSize })}</td>
                  <td>
                    {r.assignments.length === 0 ? (
                      <span className={s.unknown}>{t("nothing")}</span>
                    ) : (
                      r.assignments.map((a) => (
                        <div key={a.id}>
                          {a.flight ? flightName(a.flight) : a.flightId}{" "}
                          <span className={s.why}>
                            {hhmm(a.from)}–{hhmm(a.to)} {a.actual ? t("(there now)") : t("(planned)")}
                          </span>
                        </div>
                      ))
                    )}
                  </td>
                  <td>
                    {r.blocked.length > 0 ? (
                      <span className={s.bad}>{r.blocked.map((b) => `${b.ref ?? ""} ${b.why}`).join("; ")}</span>
                    ) : r.cautions.length > 0 ? (
                      /* work on the surface, which is worth knowing and does not close it */
                      <span className={s.caution}>{r.cautions.map((b) => `${b.ref ?? ""} ${b.why}`).join("; ")}</span>
                    ) : r.stand.outOfService ? (
                      <span className={s.bad}>{r.stand.outOfService.why}</span>
                    ) : r.conflicts.length > 0 ? (
                      <span className={s.bad}>{tn(r.conflicts.length, "{n} conflict", "{n} conflicts")}</span>
                    ) : r.stand.excludes?.length ? (
                      <span className={s.why}>
                        {t("cannot be used with {list}", { list: r.stand.excludes.join(", ") })}
                      </span>
                    ) : (
                      <span className={s.good}>{t("open")}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>

        {rows.every((r) => r.assignments.length === 0) && (
          <EmptyState>{t("Nothing is planned on any stand today.")}</EmptyState>
        )}
        {saved && (
          <p className={saved.ok ? ex.saved : s.bad} role={saved.ok ? undefined : "alert"} data-testid="stands-saved">
            {saved.text}
          </p>
        )}
      </Page>
    </FlightTheme>
  );
}
